import React, { useMemo, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import ProductPerformanceTrend from '../ProductPerformance/ProductPerformanceTrend';

import LebtCommercialBusiness from './LebtCommercialBusiness';
import LebtCommercialFilters from './LebtCommercialFilters';
import { metricText, trendOption } from './charts';
import {
  type CardProblem,
  type CardResult,
  type Kpis,
  LEBT_COMMERCIAL_PAGE_ID,
  type LebtFilters,
  type Metric,
  TABS,
  type Tab as TabValue,
  bucketEnd,
  bucketOf,
  businessesOf,
  chartLines,
  comparisonWindow,
  filtersError,
  isProblem,
  kpiPair,
  lensFilters,
  orderLines,
  ordersSummary,
  parseFilters,
  percentChange,
  readCard,
  sydneyToday,
  trendOf,
  weeklySummary,
  writeFilters,
} from './model';
import { editFromBridge, lebtCommercialPageView } from './pageAdapter';

const LENS_ID = nativeLensId(LEBT_COMMERCIAL_PAGE_ID);
const SOURCE = 'analytics.lebt_sales_with_companies';
const BUSINESS_SOURCE = 'exports_salesforce.lebt_business_activity_status';

const KPI_CARDS: ReadonlyArray<{ label: string; key: keyof Kpis; metric: Metric }> = [
  { label: 'Purchasers', key: 'purchasers', metric: 'Purchasers' },
  { label: 'Orders', key: 'orders', metric: 'Orders' },
  { label: 'Gross Sales AUD', key: 'grossSales', metric: 'Gross Sales' },
  { label: 'Margin AUD', key: 'margin', metric: 'Margin' },
];

const csvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
function download(name: string, lines: Array<Array<string | number>>) {
  const contents = '﻿' + lines.map((line) => line.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function LebtCommercialContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<LebtFilters>, replace = false) => {
    navigate(
      {
        pathname: location.pathname,
        search: writeFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
        hash: location.hash,
      },
      { replace },
    );
  };
  const error = filtersError(filters);
  // Filter values come back with each load; the last full list stays in place while the next one loads.
  const lastOptions = useRef<Record<string, Array<string>>>({});
  const request = useMemo(() => lensFilters(filters, lastOptions.current), [filters]);
  const query = useGetReportingLeLensData(error ? undefined : LENS_ID, request);
  const cards = query.data?.cards ?? [];
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const card = (id: string) => readCard(cards, id);

  const kpiResult = card('kpis');
  const kpis = isProblem(kpiResult) ? null : kpiPair(kpiResult);
  const linesResult = card('order_lines');
  const lines = useMemo(() => (isProblem(linesResult) ? [] : orderLines(linesResult)), [linesResult]);
  const chartResult = card('chart_lines');
  const trend = useMemo(
    () =>
      isProblem(chartResult) ? null : trendOf(chartLines(chartResult), filters.cadence, filters.metric, filters.split),
    [chartResult, filters.cadence, filters.metric, filters.split],
  );
  const businessResult = card('business_rows');
  const businesses = useMemo(() => (isProblem(businessResult) ? [] : businessesOf(businessResult)), [businessResult]);
  const summary = ordersSummary(lines, filters.metric);
  const weeks = weeklySummary(lines);
  const comparison = comparisonWindow(filters);
  const range = `${filters.from} to ${filters.to}`;
  const comparisonRange = `${comparison.from} to ${comparison.to}`;

  const pageView = lebtCommercialPageView({
    ready,
    filters,
    options,
    period: range,
    comparisonPeriod: comparisonRange,
    current: kpis?.current ?? null,
    previous: kpis?.previous ?? null,
    sections:
      filters.tab === 'business'
        ? [
            {
              title: 'Accounts Summary',
              rows: businesses.map((business) => ({
                label: business.name,
                values: {
                  joined: business.joined,
                  lastTransaction: business.lastTransaction,
                  activity: business.activity,
                  orders: business.orders,
                },
              })),
              truncated: false,
            },
          ]
        : [
            {
              title: `Orders Summary (${filters.metric})`,
              rows: summary.rows.map((row) => ({
                label: `${row.business} · ${row.order}`,
                values: { ...row.values, total: row.total },
              })),
              truncated: !isProblem(linesResult) && linesResult.truncated,
            },
            {
              title: 'Weekly Summary',
              rows: weeks.map((week) => ({
                label: week.week,
                values: {
                  grossSalesInclCredit: week.grossSalesInclCredit,
                  margin: week.margin,
                  orders: week.orders,
                  purchasers: week.purchasers,
                },
              })),
              truncated: !isProblem(linesResult) && linesResult.truncated,
            },
          ],
  });
  usePageBridge({
    pageId: LEBT_COMMERCIAL_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters, options), true);
    },
  });

  const problem = (result: CardResult | CardProblem) =>
    isProblem(result) ? (
      <Alert severity={query.isFetching ? 'info' : 'error'}>{query.isFetching ? 'Loading…' : result.problem}</Alert>
    ) : null;
  const rowLimit = (result: CardResult | CardProblem) =>
    !isProblem(result) && result.truncated ? (
      <Alert severity="warning">
        This section reached its row limit, so some rows are missing. Narrow the dates or filters.
      </Alert>
    ) : null;
  // The workbook's "Filter 2" action: picking a bar filters the page to that cadence period.
  const selectBucket = (bucket?: string) => {
    if (!bucket) {
      return;
    }
    const start = bucketOf(bucket, filters.cadence);
    const end = bucketEnd(start, filters.cadence);
    const lastDay = parseFilters(new URLSearchParams()).to;
    update({ from: start, to: end > lastDay ? lastDay : end });
  };
  const csvLines = () => [
    ['Report', 'LEBT Commercial Dashboard · Orders'],
    ['Source', SOURCE],
    ['Period', range],
    [
      'Date',
      'Business',
      'Order',
      'Category',
      'Product',
      'Product bk',
      'Order item status',
      'Gross Sales AUD',
      'Margin AUD',
      'Gross Sales incl GC and PC AUD',
      'Gift Card Credit AUD',
      'Purchased Credit AUD',
      'Order items',
    ],
    ...[...lines]
      .sort((a, b) => a.date.localeCompare(b.date) || a.businessName.localeCompare(b.businessName))
      .map((line) => [
        line.date,
        line.businessName,
        line.order,
        line.category,
        line.product,
        line.productBk,
        line.status,
        line.grossSales,
        line.margin,
        line.grossSalesInclCredit,
        line.giftCard,
        line.purchasedCredit,
        line.items,
      ]),
  ];

  const kpiPanel = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        gap: 1.5,
      }}
    >
      {KPI_CARDS.map((kpi) => {
        const current = kpis?.current[kpi.key] ?? null;
        const previous = kpis?.previous?.[kpi.key] ?? null;
        const change = percentChange(current, previous);
        return (
          <Paper key={kpi.key} variant="outlined" sx={{ p: 1.5, minWidth: 0, bgcolor: tokens.surface }}>
            <Typography variant="caption" color="text.secondary">
              {kpi.label}
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: tokens.accent, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
            >
              {kpis ? metricText(kpi.metric, current) : '…'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: change === null ? 'text.secondary' : change < 0 ? 'error.main' : 'success.main' }}
            >
              {change === null ? '►' : change > 0 ? '▲+' : change < 0 ? '▼' : '►'}
              {change === null ? '' : `${(change * 100).toFixed(1)}%`} vs {metricText(kpi.metric, previous)}
            </Typography>
            {kpi.key === 'grossSales' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Incl GC and PC (not TTV): {metricText('Gross Sales', kpis?.current.grossSalesInclCredit ?? null)}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Box>
  );

  const money = (metric: Metric, value: number | undefined) => (value === undefined ? '' : metricText(metric, value));
  const ordersTab = (
    <Stack gap={2}>
      {problem(kpiResult)}
      {kpiPanel}
      <Typography variant="caption" color="text.secondary">
        {range} vs {comparisonRange} (
        {filters.comparison === 'PP'
          ? 'previous period'
          : filters.comparison === 'LY'
            ? 'same dates last year'
            : 'custom'}
        )
      </Typography>
      {problem(chartResult)}
      {rowLimit(chartResult)}
      {trend && (
        <ProductPerformanceTrend
          title={`${filters.metric} by ${filters.cadence.toLowerCase()} period${filters.split === 'All' ? '' : ` · split by ${filters.split}`}`}
          option={trendOption(
            trend,
            filters.metric,
            { from: bucketOf(filters.from, filters.cadence), to: bucketOf(filters.to, filters.cadence) },
            tokens.accent,
          )}
          source={SOURCE}
          range={`1 Jan last year to ${filters.to} · shaded: selected period · select a bar to filter to it`}
          height={320}
          onSelect={(selection) => selectBucket(selection.date)}
        />
      )}
      {problem(linesResult)}
      {rowLimit(linesResult)}
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1, bgcolor: tokens.plateHover }}
        >
          <Typography component="h2" variant="h6" sx={{ fontSize: 17 }}>
            Orders Summary · {filters.metric === 'Gross Sales' ? 'Gross Sales incl GC and PC AUD' : filters.metric}
          </Typography>
          <Button
            size="small"
            disabled={!ready}
            onClick={() => download(`lebt-orders-${filters.from}-${filters.to}.csv`, csvLines())}
          >
            CSV
          </Button>
        </Stack>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Business name</TableCell>
                <TableCell>Order</TableCell>
                {summary.categories.map((category) => (
                  <TableCell key={category} align="right">
                    {category}
                  </TableCell>
                ))}
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.rows.map((row) => (
                <TableRow key={`${row.business}-${row.order}`} hover>
                  <TableCell>{row.business}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.order}</TableCell>
                  {summary.categories.map((category) => (
                    <TableCell key={category} align="right">
                      {money(filters.metric, row.values[category])}
                    </TableCell>
                  ))}
                  <TableCell align="right">{metricText(filters.metric, row.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <Typography component="h2" variant="h6" sx={{ px: 2, py: 1.5, fontSize: 17, bgcolor: tokens.plateHover }}>
          Weekly Summary
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Week starting</TableCell>
                {weeks.map((week) => (
                  <TableCell key={week.week} align="right">
                    {week.week}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(
                [
                  ['Gross Sales incl GC and PC AUD', 'grossSalesInclCredit', 'Gross Sales'],
                  ['Margin AUD', 'margin', 'Margin'],
                  ['Orders', 'orders', 'Orders'],
                  ['Purchasers', 'purchasers', 'Purchasers'],
                ] as const
              ).map(([label, key, metric]) => (
                <TableRow key={key}>
                  <TableCell>{label}</TableCell>
                  {weeks.map((week) => (
                    <TableCell key={week.week} align="right">
                      {metricText(metric, week[key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              LEBT Commercial Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              LE Business Traveller orders and businesses · Sydney dates · AUD
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="center">
            <PageChatButton />
          </Stack>
        </Stack>
        <Tabs value={filters.tab} onChange={(_, tab: TabValue) => update({ tab })} aria-label="LEBT Commercial views">
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" variant="outlined" color="primary" label="Live" />
          <Typography variant="caption" color="text.secondary">
            {filters.tab === 'business' ? BUSINESS_SOURCE : SOURCE} · rebuilt daily · last update {sydneyToday()} ·{' '}
            {query.isFetching ? 'updating…' : range}
          </Typography>
        </Stack>
        {filters.tab === 'orders' && <LebtCommercialFilters filters={filters} options={options} onChange={update} />}
        {error && <Alert severity="error">{error}</Alert>}
        {failure && (
          <Alert
            severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
            action={
              <Button color="inherit" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {failure.status === 404
              ? 'LEBT Commercial is not registered in Library for this environment yet. A reporting admin registers it under Playground → Lens pages → Register native pages.'
              : failure.status === 403
                ? 'You do not have access to LEBT Commercial. Ask a reporting admin to add you to its Library folder.'
                : 'LEBT Commercial could not load.'}
          </Alert>
        )}
        {filters.tab === 'orders' ? (
          ordersTab
        ) : (
          <>
            {problem(businessResult)}
            {!isProblem(businessResult) && <LebtCommercialBusiness businesses={businesses} source={BUSINESS_SOURCE} />}
          </>
        )}
      </Stack>
    </Container>
  );
}

export default function LebtCommercialPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="lebt-commercial"
      title="LEBT Commercial Dashboard"
      printTarget={printTarget}
    >
      <LebtCommercialContent />
    </NativeReportShell>
  );
}
